/**
 * 레이지 무브 제스처 컨트롤러 — RageDock(좌측 아이콘)에서 모드를 켜면 포인터 제스처로 발동.
 *  - sweep: 책상 위를 누르면 시작 쪽에 따라 팔이 등장하고, 누른 채 드래그하면 팔이 포인터를
 *    따라 움직이며 지나간 물건을 팔 이동속도에 비례해 쳐낸다(RageMoves.stepSweep — 고정 스텝).
 *    살살 쓸면 살짝 밀리고, 세게 쓸면 튕겨나가 벽/바닥 충돌로 깨진다. 임계값 트리거 없음.
 *  - flip: 책상 앞쪽(z>0)을 잡고 위로 드래그. 드래그양에 비례해 상판이 뒷모서리를 축으로
 *    기울어지는 선행 연출 후, 임계값을 넘으면 RageMoves.flip() 발동.
 * 모드가 켜져 있는 동안 InputController는 isInteractive 콜백으로 비활성화된다(main 배선).
 * 발동/취소 시 모드는 자동 해제되고 onModeChange로 UI에 알린다.
 */
import * as THREE from 'three';
import type { EventBus } from '../core/EventBus';
import type { RageMoves } from '../gameplay/RageMoves';
import { BALANCE } from '../config/balance';
import { LAYOUT } from '../config/layout';
import { sweepDirForStart, type SweepDir } from '../gameplay/rageLogic';
import { RageArm } from '../render/RageArm';

export type RageMode = 'sweep' | 'flip' | null;

/**
 * 뒤집기 선행 연출 최대 기울기(rad ≈ 6.3°). 시각 전용 기울임이라 물리 물건은 제자리에
 * 남는다 — 각도가 크면 앞줄 물건이 상판에 파묻혀 보여서 작게 유지한다.
 */
const MAX_TILT = 0.11;
const X_AXIS = new THREE.Vector3(1, 0, 0);

export class RageGestures {
  mode: RageMode = null;
  /** 모드 변경 통지(RageDock가 활성 표시/힌트에 사용). */
  onModeChange: (m: RageMode) => void = () => {};

  private raycaster = new THREE.Raycaster();
  private ndc = new THREE.Vector2();
  /** 책상 윗면 평면(y=topY) — 스윕 포인터의 월드 x 좌표 계산용. */
  private deskPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -LAYOUT.desk.topY);
  private dragPlane = new THREE.Plane();
  private hit = new THREE.Vector3();
  private arm: RageArm;
  private phase: 'idle' | 'sweep' | 'flip' = 'idle';
  private sweepDir: SweepDir = 1;
  private flipStartY = 0;
  /** 뒤집기 선행 기울기(현재값/목표값) — update()에서 스프링 보간. */
  private tilt = 0;
  private tiltTarget = 0;
  /** 기울일 메쉬(상판/매트)와 원래 위치 — 뒷모서리 축 회전 계산 기준. */
  private tiltTargets: { mesh: THREE.Mesh; pos: THREE.Vector3 }[];
  private tmp = new THREE.Vector3();

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: THREE.PerspectiveCamera,
    scene: THREE.Scene,
    private deskMesh: THREE.Mesh,
    deskPad: THREE.Mesh,
    private rage: RageMoves,
    bus: EventBus,
    private isPlaying: () => boolean,
  ) {
    this.arm = new RageArm(scene);
    this.tiltTargets = [
      { mesh: deskMesh, pos: deskMesh.position.clone() },
      { mesh: deskPad, pos: deskPad.position.clone() },
    ];
    canvas.addEventListener('pointerdown', this.onDown);
    canvas.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);

    const off = (): void => this.setMode(null);
    bus.on('session:reset', off);
    bus.on('session:end', off);
    // 책상이 사라지면(내리치기 반파 포함) 뒤집기 모드는 무의미 — 즉시 해제.
    bus.on('desk:destroyed', () => {
      if (this.mode === 'flip') this.setMode(null);
    });
  }

  /** 모드 설정(null = 일반 잡기 모드). 진행 중이던 제스처는 취소된다. */
  setMode(m: RageMode): void {
    if (this.mode === m) return;
    this.cancelGesture();
    this.mode = m;
    this.canvas.classList.toggle('mode-sweep', m === 'sweep');
    this.canvas.classList.toggle('mode-flip', m === 'flip');
    this.onModeChange(m);
  }

  /** 진행 중 제스처 취소(팔 숨김 + 기울기 원복 + 쓸기 종료). 모드는 유지. */
  private cancelGesture(): void {
    this.rage.endSweep(); // 진행 중 쓸기 중단(비활성이면 무해)
    this.arm.hide();
    this.phase = 'idle';
    this.tiltTarget = 0;
    this.tilt = 0;
    this.applyTilt(0);
  }

  private setNdc(e: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  /** 현재 포인터 레이와 plane의 교점 → this.hit. 실패 시 false. */
  private intersect(e: PointerEvent, plane: THREE.Plane): boolean {
    this.setNdc(e);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    return this.raycaster.ray.intersectPlane(plane, this.hit) !== null;
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.mode || !this.isPlaying()) return;
    if (this.mode === 'sweep') {
      if (!this.intersect(e, this.deskPlane)) return;
      this.sweepDir = sweepDirForStart(this.hit.x);
      this.rage.beginSweep(this.sweepDir, this.hit.x);
      this.arm.show(this.sweepDir, this.hit.x);
      this.phase = 'sweep';
      this.canvas.setPointerCapture?.(e.pointerId);
      return;
    }
    // flip: 책상 앞쪽(z>0)을 잡아야 시작.
    this.setNdc(e);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hit = this.raycaster.intersectObject(this.deskMesh)[0];
    if (!hit || hit.point.z <= 0) return;
    this.camera.getWorldDirection(this.dragPlane.normal);
    this.dragPlane.setFromNormalAndCoplanarPoint(this.dragPlane.normal, hit.point);
    this.flipStartY = hit.point.y;
    this.phase = 'flip';
    this.canvas.setPointerCapture?.(e.pointerId);
  };

  private onMove = (e: PointerEvent): void => {
    if (this.phase === 'sweep') {
      if (!this.intersect(e, this.deskPlane)) return;
      // 손 위치만 갱신 — 실제 배팅은 고정 스텝(stepSweep)이 팔 이동속도로 판정한다.
      this.rage.setSweepHand(this.hit.x);
      this.arm.follow(this.hit.x);
      return;
    }
    if (this.phase !== 'flip') return;
    if (!this.intersect(e, this.dragPlane)) return;
    const dy = Math.max(0, this.hit.y - this.flipStartY);
    const limit = BALANCE.rage.gesture.flipDragY;
    this.tiltTarget = MAX_TILT * Math.min(1, dy / limit);
    if (dy >= limit) {
      this.tiltTarget = 0;
      this.tilt = 0;
      this.applyTilt(0); // DeskBreak가 상판을 교체하기 전에 원래 자세로 복원
      this.rage.flip();
      this.phase = 'idle';
      this.setMode(null);
    }
  };

  private onUp = (): void => {
    if (this.phase === 'sweep') {
      const didSweep = this.rage.endSweep();
      this.arm.release(); // 관성 팔로우스루 후 사라짐
      this.phase = 'idle';
      // 물건을 하나라도 쳐냈으면 쿨다운(rage:sweep는 이미 발행됨) → 모드 해제.
      // 아무것도 못 쳐냈으면(그냥 탭) 모드 유지 — 다시 시도 가능.
      if (didSweep) this.setMode(null);
      return;
    }
    if (this.phase === 'flip') this.tiltTarget = 0; // update()가 부드럽게 원복
    this.phase = 'idle';
  };

  /** 매 프레임(렌더 dt): 팔 애니메이션 + 기울기 스프링 보간. */
  update(dt: number): void {
    this.arm.update(dt);
    const diff = this.tiltTarget - this.tilt;
    if (Math.abs(diff) > 1e-4 || this.tilt > 1e-4) {
      this.tilt = Math.abs(diff) < 1e-4 ? this.tiltTarget : this.tilt + diff * Math.min(1, dt * 16);
      this.applyTilt(this.tilt);
    }
  }

  /** 상판/매트를 뒷모서리(y=topY, z=-d/2) 축으로 θ만큼 기울인다(θ=0이면 원복). */
  private applyTilt(theta: number): void {
    const pivotY = LAYOUT.desk.topY;
    const pivotZ = -LAYOUT.desk.size[2] / 2;
    for (const { mesh, pos } of this.tiltTargets) {
      if (theta === 0) {
        mesh.position.copy(pos);
        mesh.rotation.x = 0;
        continue;
      }
      // 앞모서리(+z)가 들리려면 x축 회전각은 음수여야 한다(y' = -z·sinα).
      this.tmp.set(pos.x, pos.y - pivotY, pos.z - pivotZ).applyAxisAngle(X_AXIS, -theta);
      mesh.position.set(this.tmp.x, this.tmp.y + pivotY, this.tmp.z + pivotZ);
      mesh.rotation.x = -theta;
    }
  }
}
